import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  IndianRupee,
  Percent,
  Calendar,
  Users,
  TrendingUp,
  ArrowDownLeft,
  ArrowUpRight,
  Search,
  Plus,
  History,
  Check,
  X,
  Image,
  Clock,
  Settings2,
  AlertCircle,
  CreditCard,
  LogIn,
  Trash2,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const COMMISSION_TYPES = [
  { id: "percentage", name: "Percentage per Ride", icon: Percent, description: "% of net fare" },
  { id: "flat_per_ride", name: "Flat per Ride", icon: IndianRupee, description: "Fixed amount per ride" },
  { id: "fixed_daily", name: "Fixed Daily", icon: Calendar, description: "Per day (if rides > 0)" },
  { id: "fixed_weekly", name: "Fixed Weekly", icon: Calendar, description: "Per week (if rides > 0)" },
  { id: "fixed_monthly", name: "Fixed Monthly", icon: Calendar, description: "Per month (if rides > 0)" },
];

const PAYMENT_TYPES = [
  { id: "driver_to_admin", name: "Driver Paid You", icon: ArrowDownLeft, color: "text-green-600" },
  { id: "admin_to_driver", name: "You Paid Driver", icon: ArrowUpRight, color: "text-red-600" },
  { id: "admin_to_city_admin", name: "You Paid City Admin", icon: ArrowUpRight, color: "text-orange-600" },
];

const PAYMENT_METHODS = [
  { id: "cash", name: "Cash" },
  { id: "upi", name: "UPI" },
  { id: "bank_transfer", name: "Bank Transfer" },
  { id: "wallet", name: "Wallet" },
  { id: "other", name: "Other" },
];

export const CommissionManagement = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("settings");
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [cityAdmins, setCityAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingPayments, setPendingPayments] = useState([]);
  const [viewingProof, setViewingProof] = useState(null);
  const [changeRequests, setChangeRequests] = useState([]);
  const [savingCommissionOptions, setSavingCommissionOptions] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [commissionOptions, setCommissionOptions] = useState({
    percentage_enabled: true,
    flat_per_ride_enabled: false,
    fixed_daily_enabled: false,
    fixed_weekly_enabled: false,
    fixed_monthly_enabled: true,
  });
  
  // Payment settings for subscription switching
  const [paymentSettings, setPaymentSettings] = useState({
    commission_payment_mode: "prepaid",
    razorpay_key_id: "",
    razorpay_key_secret: "",
    has_razorpay_configured: false
  });
  const [savingPaymentSettings, setSavingPaymentSettings] = useState(false);
  
  // Dialogs
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showVerifyPaymentDialog, setShowVerifyPaymentDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    payment_type: "driver_to_admin",
    recipient_id: "",
    amount: "",
    payment_method: "cash",
    reference_number: "",
    notes: ""
  });
  
  // Driver search and filter for payment dialog
  const [driverSearch, setDriverSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [selectedDriverDue, setSelectedDriverDue] = useState(null);
  const [loadingDriverDue, setLoadingDriverDue] = useState(false);
  const [cities, setCities] = useState([]);
  
  // Sorting state for Drivers tab
  const [driversSortConfig, setDriversSortConfig] = useState({ key: 'name', direction: 'asc' });
  
  // Pagination state for Drivers tab
  const [driversPage, setDriversPage] = useState(1);
  const [driversTotalPages, setDriversTotalPages] = useState(1);
  const [driversTotalCount, setDriversTotalCount] = useState(0);
  const [driversLoading, setDriversLoading] = useState(false);
  const DRIVERS_PER_PAGE = 20;

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [user?.id]);
  
  // Auto-refresh pending payments every 15 seconds
  useEffect(() => {
    if (!user?.id) return;
    
    const refreshPendingPayments = async () => {
      try {
        const res = await axios.get(`${API}/admin/${user.id}/pending-payments`);
        setPendingPayments(res.data || []);
      } catch (err) {
        console.log('Auto-refresh pending payments failed:', err);
      }
    };
    
    const interval = setInterval(refreshPendingPayments, 15000);
    return () => clearInterval(interval);
  }, [user?.id]);

  const fetchData = async () => {
    try {
      const [settingsRes, summaryRes, paymentsRes, driversRes, cityAdminsRes, pendingRes, optionsRes, changeReqRes, paymentSettingsRes, citiesRes] = await Promise.all([
        axios.get(`${API}/admin/${user.id}/commission-settings`),
        axios.get(`${API}/admin/${user.id}/commission-summary`),
        axios.get(`${API}/admin/${user.id}/payments?limit=50`),
        axios.get(`${API}/admin/drivers?admin_id=${user.id}`),
        axios.get(`${API}/admin/city-admins?admin_id=${user.id}`),
        axios.get(`${API}/admin/${user.id}/pending-payments`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/${user.id}/commission-options`).catch(() => ({ data: { options: {} } })),
        axios.get(`${API}/admin/${user.id}/commission-change-requests`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/${user.id}/payment-settings`).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/${user.id}/cities`).catch(() => ({ data: [] }))
      ]);
      
      setSettings(settingsRes.data.settings);
      setSummary(summaryRes.data);
      setPayments(paymentsRes.data.payments || []);
      setDrivers(driversRes.data || []);
      setCityAdmins(cityAdminsRes.data || []);
      setPendingPayments(pendingRes.data || []);
      setCommissionOptions(optionsRes.data.options || {
        percentage_enabled: true,
        flat_per_ride_enabled: false,
        fixed_daily_enabled: false,
        fixed_weekly_enabled: false,
        fixed_monthly_enabled: true,
      });
      setChangeRequests(changeReqRes.data || []);
      setPaymentSettings({
        commission_payment_mode: paymentSettingsRes.data.commission_payment_mode || "prepaid",
        razorpay_key_id: paymentSettingsRes.data.razorpay_key_id || "",
        razorpay_key_secret: "",
        has_razorpay_configured: paymentSettingsRes.data.has_razorpay_configured || false
      });
      // Sort cities alphabetically
      const sortedCities = (citiesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
      
      // Fetch paginated drivers separately
      await fetchPaginatedDrivers(1, "");
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load commission data");
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch paginated drivers with search
  const fetchPaginatedDrivers = async (page = 1, search = "") => {
    setDriversLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", DRIVERS_PER_PAGE.toString());
      if (search) params.append("search", search);
      
      const res = await axios.get(`${API}/admin/${user.id}/drivers/paginated?${params}`);
      setDrivers(res.data.drivers || []);
      setDriversPage(res.data.page || 1);
      setDriversTotalPages(res.data.total_pages || 1);
      setDriversTotalCount(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch drivers:", err);
      // Fallback to old endpoint if paginated doesn't exist
      try {
        const res = await axios.get(`${API}/admin/drivers?admin_id=${user.id}`);
        setDrivers(res.data || []);
        setDriversTotalCount(res.data?.length || 0);
        setDriversTotalPages(1);
      } catch (fallbackErr) {
        console.error("Fallback also failed:", fallbackErr);
      }
    } finally {
      setDriversLoading(false);
    }
  };
  
  // Debounced search for drivers
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (user?.id) {
        fetchPaginatedDrivers(1, searchTerm);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, user?.id]);

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/admin/${user.id}/commission-settings`, settings);
      toast.success("Commission settings saved!");
      fetchData();
    } catch (err) {
      toast.error("Failed to save settings");
    }
  };

  const handleSaveCommissionOptions = async () => {
    setSavingCommissionOptions(true);
    try {
      await axios.put(`${API}/admin/${user.id}/commission-options`, commissionOptions);
      toast.success("Commission plan options saved successfully!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save commission options");
    } finally {
      setSavingCommissionOptions(false);
    }
  };

  const handleSavePaymentSettings = async () => {
    setSavingPaymentSettings(true);
    try {
      const payload = {
        commission_payment_mode: paymentSettings.commission_payment_mode
      };
      // Only send keys if they have values (don't overwrite with empty)
      if (paymentSettings.razorpay_key_id) {
        payload.razorpay_key_id = paymentSettings.razorpay_key_id;
      }
      if (paymentSettings.razorpay_key_secret) {
        payload.razorpay_key_secret = paymentSettings.razorpay_key_secret;
      }
      
      await axios.put(`${API}/admin/${user.id}/payment-settings`, payload);
      toast.success("Payment settings saved!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save payment settings");
    } finally {
      setSavingPaymentSettings(false);
    }
  };

  const handleVerifyPayment = async (requestId, verified) => {
    try {
      await axios.put(`${API}/admin/commission-change-requests/${requestId}/verify-payment?verified=${verified}`);
      toast.success(verified ? "Payment verified!" : "Payment rejected");
      setShowVerifyPaymentDialog(false);
      setSelectedRequest(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to verify payment");
    }
  };

  const handleApproveChangeRequest = async (requestId, subscriptionDays = 30) => {
    try {
      await axios.put(`${API}/admin/commission-change-requests/${requestId}/approve?subscription_days=${subscriptionDays}`);
      toast.success("Commission change approved and applied!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve change request");
    }
  };

  const handleRejectChangeRequest = async (requestId) => {
    if (!window.confirm("Reject this commission change request?")) return;
    try {
      await axios.put(`${API}/admin/commission-change-requests/${requestId}/reject`);
      toast.success("Change request rejected");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject request");
    }
  };

  const handleApprovePayment = async (paymentId) => {
    try {
      await axios.put(`${API}/admin/payments/${paymentId}/approve`);
      toast.success("Payment approved!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve payment");
    }
  };

  const handleRejectPayment = async (paymentId) => {
    if (!window.confirm("Reject this payment? The amount will go back to driver's dues.")) return;
    try {
      await axios.put(`${API}/admin/payments/${paymentId}/reject`);
      toast.success("Payment rejected. Amount added back to dues.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject payment");
    }
  };

  const viewPaymentProof = async (paymentId) => {
    try {
      const res = await axios.get(`${API}/admin/payments/${paymentId}/proof`);
      if (res.data.proof_photo) {
        setViewingProof(res.data.proof_photo);
      } else {
        toast.info("No proof image attached");
      }
    } catch (err) {
      toast.error("Failed to load proof");
    }
  };

  // Fetch driver's due amount when selected
  const fetchDriverDue = async (driverId) => {
    if (!driverId) {
      setSelectedDriverDue(null);
      return;
    }
    
    setLoadingDriverDue(true);
    try {
      const res = await axios.get(`${API}/drivers/${driverId}/earnings`);
      const earnings = res.data;
      const dueAmount = earnings?.owed_status?.amount_owed || earnings?.pending?.owe_to_admin || 0;
      setSelectedDriverDue({
        amount: dueAmount,
        driver_name: earnings?.driver_name || drivers.find(d => d.id === driverId)?.name || "Driver"
      });
      // Auto-fill the amount field with the due amount
      setPaymentForm(prev => ({...prev, amount: dueAmount.toFixed(2)}));
    } catch (err) {
      console.error("Error fetching driver due:", err);
      setSelectedDriverDue(null);
    } finally {
      setLoadingDriverDue(false);
    }
  };

  // Handle driver selection in payment dialog
  const handleDriverSelect = (driverId) => {
    setPaymentForm({...paymentForm, recipient_id: driverId});
    if (paymentForm.payment_type === "driver_to_admin") {
      fetchDriverDue(driverId);
    }
  };

  // Filter drivers based on search and city
  const filteredPaymentDrivers = drivers.filter(d => {
    const searchLower = driverSearch.toLowerCase();
    const matchesSearch = !driverSearch || 
      d.name?.toLowerCase().includes(searchLower) ||
      d.phone?.includes(driverSearch);
    const matchesCity = !cityFilter || 
      d.city_id === cityFilter || 
      d.assigned_city_id === cityFilter;
    return matchesSearch && matchesCity;
  });

  const handleRecordPayment = async () => {
    if (!paymentForm.recipient_id || !paymentForm.amount) {
      toast.error("Please select recipient and enter amount");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/${user.id}/payments/record`, paymentForm);
      toast.success("Payment recorded successfully!");
      setShowPaymentDialog(false);
      setPaymentForm({
        payment_type: "driver_to_admin",
        recipient_id: "",
        amount: "",
        payment_method: "cash",
        reference_number: "",
        notes: ""
      });
      setDriverSearch("");
      setCityFilter("");
      setSelectedDriverDue(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    }
  };

  const handleSaveOverride = async () => {
    if (!selectedEntity) return;
    
    try {
      const endpoint = selectedEntity.type === "driver" 
        ? `${API}/admin/driver/${selectedEntity.id}/commission`
        : `${API}/admin/city-admin/${selectedEntity.id}/commission`;
      
      await axios.put(endpoint, selectedEntity.override);
      toast.success("Commission override saved!");
      setShowOverrideDialog(false);
      setSelectedEntity(null);
    } catch (err) {
      toast.error("Failed to save override");
    }
  };

  const openOverrideDialog = async (entity, type) => {
    try {
      const endpoint = type === "driver"
        ? `${API}/admin/driver/${entity.id}/commission`
        : `${API}/admin/city-admin/${entity.id}/commission`;
      
      const res = await axios.get(endpoint);
      setSelectedEntity({
        ...entity,
        type,
        override: res.data.override || {},
        effective: res.data.effective_settings,
        adminDefault: res.data.admin_default
      });
      setShowOverrideDialog(true);
    } catch (err) {
      toast.error("Failed to load commission details");
    }
  };

  // Login As Driver
  const handleLoginAsDriver = async (driver) => {
    if (impersonating) return;
    setImpersonating(true);
    
    try {
      const res = await axios.post(`${API}/admin/${user.id}/login-as-driver/${driver.id}`);
      
      // Store current admin session before switching
      const currentToken = localStorage.getItem("biketaxi_token");
      localStorage.setItem("original_admin_user", JSON.stringify(user));
      localStorage.setItem("original_admin_token", currentToken);
      localStorage.setItem("is_impersonating", "true");
      
      // Login as the driver
      login(res.data.token, res.data.user);
      toast.success(`Now logged in as ${driver.name}`);
      
      // Navigate to driver dashboard
      navigate("/driver");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to login as driver");
    } finally {
      setImpersonating(false);
    }
  };

  // Remove driver from admin's list
  const handleRemoveDriver = async (driver, permanently = false) => {
    const action = permanently ? "permanently delete" : "remove";
    const warning = permanently 
      ? `This will PERMANENTLY DELETE ${driver.name}'s account. This cannot be undone!`
      : `This will remove ${driver.name} from your driver list. They can request to join again.`;
    
    if (!window.confirm(`${warning}\n\nAre you sure you want to ${action} this driver?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/${user.id}/drivers/${driver.id}?permanently_delete=${permanently}`);
      toast.success(permanently 
        ? `Driver "${driver.name}" has been permanently deleted`
        : `Driver "${driver.name}" has been removed from your list`
      );
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove driver");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="commission-management">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Revenue & Commission</h2>
          <p className="text-muted-foreground">Manage commission rates and record payments</p>
        </div>
        <Button onClick={() => setShowPaymentDialog(true)}>
          <Plus className="w-4 h-4 mr-2" /> Record Payment
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="p-3 md:p-4 bg-card rounded-xl border border-border">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-muted-foreground">Total Rides</span>
          </div>
          <p className="text-xl md:text-2xl font-bold">{summary?.summary?.total_rides || 0}</p>
        </div>
        <div className="p-3 md:p-4 bg-green-50 rounded-xl border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <ArrowDownLeft className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-600">Received</span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-green-600">
            ₹{summary?.payments?.received_from_drivers?.toFixed(0) || 0}
          </p>
        </div>
        <div className="p-3 md:p-4 bg-amber-50 rounded-xl border border-amber-200">
          <div className="flex items-center gap-2 mb-2">
            <ArrowUpRight className="w-4 h-4 text-amber-600" />
            <span className="text-xs text-amber-600">Paid Out</span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-amber-600">
            ₹{((summary?.payments?.paid_to_drivers || 0) + (summary?.payments?.paid_to_city_admins || 0)).toFixed(0)}
          </p>
        </div>
        <div className="p-3 md:p-4 bg-purple-50 rounded-xl border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <IndianRupee className="w-4 h-4 text-purple-600" />
            <span className="text-xs text-purple-600">Pending</span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-purple-600">
            ₹{summary?.pending?.from_drivers?.toFixed(0) || 0}
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full md:w-auto flex flex-wrap">
          <TabsTrigger value="settings" className="flex-1 md:flex-none">Settings</TabsTrigger>
          <TabsTrigger value="change-requests" className="flex-1 md:flex-none relative">
            Requests
            {changeRequests.filter(r => r.status === "pending").length > 0 && (
              <span className="ml-1 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {changeRequests.filter(r => r.status === "pending").length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex-1 md:flex-none relative">
            Pending
            {pendingPayments.length > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {pendingPayments.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="drivers" className="flex-1 md:flex-none">Drivers</TabsTrigger>
          <TabsTrigger value="city-admins" className="flex-1 md:flex-none">City Admins</TabsTrigger>
          <TabsTrigger value="payments" className="flex-1 md:flex-none">Payments</TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Driver Commission - Vehicle Wise */}
            <div className="bg-card rounded-xl border border-border p-4 md:p-6 md:col-span-2">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" /> Driver Commission Rates (by Vehicle Type)
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Default Commission Type</Label>
                  <select
                    value={settings?.commission_type || "percentage"}
                    onChange={(e) => setSettings({...settings, commission_type: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                  >
                    {COMMISSION_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>

                {/* Vehicle-wise Rate Cards */}
                <div className="grid md:grid-cols-4 gap-4 mt-4">
                  {["bike", "auto", "car", "goods_carrier"].map((vehicleType) => {
                    const vehicleRates = settings?.vehicle_rates?.[vehicleType] || {};
                    const vehicleIcons = {
                      bike: "🏍️",
                      auto: "🛺",
                      car: "🚗",
                      goods_carrier: "🚚"
                    };
                    const vehicleLabels = {
                      bike: "Bike",
                      auto: "Auto",
                      car: "Car",
                      goods_carrier: "Goods"
                    };
                    
                    return (
                      <div key={vehicleType} className="border border-border rounded-lg p-4 bg-muted/30">
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <span className="text-xl">{vehicleIcons[vehicleType]}</span>
                          {vehicleLabels[vehicleType]}
                        </h4>
                        
                        <div className="space-y-3">
                          {/* Percentage Rate */}
                          <div>
                            <Label className="text-xs">% per Ride</Label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              value={vehicleRates?.percentage_rate ?? 10}
                              onChange={(e) => {
                                const newRates = {
                                  ...settings?.vehicle_rates,
                                  [vehicleType]: {
                                    ...(settings?.vehicle_rates?.[vehicleType] || {}),
                                    percentage_rate: parseFloat(e.target.value) || 0
                                  }
                                };
                                setSettings({...settings, vehicle_rates: newRates});
                              }}
                              className="mt-1 h-8 text-sm"
                            />
                          </div>
                          
                          {/* Flat per Ride */}
                          <div>
                            <Label className="text-xs">₹ per Ride</Label>
                            <Input
                              type="number"
                              min="0"
                              value={vehicleRates?.flat_per_ride ?? 10}
                              onChange={(e) => {
                                const newRates = {
                                  ...settings?.vehicle_rates,
                                  [vehicleType]: {
                                    ...(settings?.vehicle_rates?.[vehicleType] || {}),
                                    flat_per_ride: parseFloat(e.target.value) || 0
                                  }
                                };
                                setSettings({...settings, vehicle_rates: newRates});
                              }}
                              className="mt-1 h-8 text-sm"
                            />
                          </div>
                          
                          {/* Fixed Daily */}
                          <div>
                            <Label className="text-xs">Daily ₹</Label>
                            <Input
                              type="number"
                              min="0"
                              value={vehicleRates?.fixed_daily ?? 100}
                              onChange={(e) => {
                                const newRates = {
                                  ...settings?.vehicle_rates,
                                  [vehicleType]: {
                                    ...(settings?.vehicle_rates?.[vehicleType] || {}),
                                    fixed_daily: parseFloat(e.target.value) || 0
                                  }
                                };
                                setSettings({...settings, vehicle_rates: newRates});
                              }}
                              className="mt-1 h-8 text-sm"
                            />
                          </div>
                          
                          {/* Fixed Weekly */}
                          <div>
                            <Label className="text-xs">Weekly ₹</Label>
                            <Input
                              type="number"
                              min="0"
                              value={vehicleRates?.fixed_weekly ?? 500}
                              onChange={(e) => {
                                const newRates = {
                                  ...settings?.vehicle_rates,
                                  [vehicleType]: {
                                    ...(settings?.vehicle_rates?.[vehicleType] || {}),
                                    fixed_weekly: parseFloat(e.target.value) || 0
                                  }
                                };
                                setSettings({...settings, vehicle_rates: newRates});
                              }}
                              className="mt-1 h-8 text-sm"
                            />
                          </div>
                          
                          {/* Fixed Monthly */}
                          <div>
                            <Label className="text-xs">Monthly ₹</Label>
                            <Input
                              type="number"
                              min="0"
                              value={vehicleRates?.fixed_monthly ?? 1500}
                              onChange={(e) => {
                                const newRates = {
                                  ...settings?.vehicle_rates,
                                  [vehicleType]: {
                                    ...(settings?.vehicle_rates?.[vehicleType] || {}),
                                    fixed_monthly: parseFloat(e.target.value) || 0
                                  }
                                };
                                setSettings({...settings, vehicle_rates: newRates});
                              }}
                              className="mt-1 h-8 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <p className="text-xs text-muted-foreground mt-2">
                  Set different commission rates for each vehicle type. The rate applied depends on the driver's vehicle type and selected commission model.
                </p>
              </div>
            </div>

            {/* City Admin Commission */}
            <div className="bg-card rounded-xl border border-border p-4 md:p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" /> City Admin Commission (Default)
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Commission Type</Label>
                  <select
                    value={settings?.city_admin_commission_type || "percentage"}
                    onChange={(e) => setSettings({...settings, city_admin_commission_type: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                  >
                    {COMMISSION_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                
                {settings?.city_admin_commission_type === "percentage" && (
                  <div>
                    <Label>Percentage Rate (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={settings?.city_admin_percentage_rate || 5}
                      onChange={(e) => setSettings({...settings, city_admin_percentage_rate: parseFloat(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                )}
                
                {settings?.city_admin_commission_type === "flat_per_ride" && (
                  <div>
                    <Label>Amount per Ride (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={settings?.city_admin_flat_per_ride || 5}
                      onChange={(e) => setSettings({...settings, city_admin_flat_per_ride: parseFloat(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                )}
                
                {(settings?.city_admin_commission_type === "fixed_daily" || 
                  settings?.city_admin_commission_type === "fixed_weekly" ||
                  settings?.city_admin_commission_type === "fixed_monthly") && (
                  <div>
                    <Label>
                      {settings?.city_admin_commission_type === "fixed_daily" ? "Daily" :
                       settings?.city_admin_commission_type === "fixed_weekly" ? "Weekly" : "Monthly"} Amount (₹)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={settings?.[`city_admin_${settings.city_admin_commission_type.replace('fixed_', '')}`] || 0}
                      onChange={(e) => setSettings({
                        ...settings, 
                        [`city_admin_${settings.city_admin_commission_type.replace('fixed_', '')}`]: parseFloat(e.target.value)
                      })}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Driver Owed Limit Settings */}
            <div className="bg-card rounded-xl border border-border p-4 md:p-6 md:col-span-2">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <IndianRupee className="w-5 h-5" /> Driver Owed Limits
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Set the maximum amount a driver can owe before they are blocked. Separate limits for Bike Taxi and Food Delivery.
              </p>
              
              {/* Bike Taxi Limits */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-orange-600 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                  Bike Taxi Commission
                </h4>
                <div className="grid md:grid-cols-2 gap-4 pl-4 border-l-2 border-orange-200">
                  <div>
                    <Label>Maximum Owed Amount (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={settings?.max_driver_owed_limit || 500}
                      onChange={(e) => setSettings({...settings, max_driver_owed_limit: parseFloat(e.target.value)})}
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Block bike taxi rides when commission owed exceeds this amount
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="block_rides"
                      checked={settings?.block_rides_on_limit !== false}
                      onChange={(e) => setSettings({...settings, block_rides_on_limit: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <div>
                      <Label htmlFor="block_rides" className="cursor-pointer">Enable Ride Blocking</Label>
                      <p className="text-xs text-muted-foreground">
                        Block drivers from accepting bike taxi rides when limit exceeded
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Food Delivery Limits */}
              <div>
                <h4 className="text-sm font-medium text-green-600 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Food Delivery (COD Collections)
                </h4>
                <div className="grid md:grid-cols-2 gap-4 pl-4 border-l-2 border-green-200">
                  <div>
                    <Label>Maximum Owed Amount (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={settings?.max_food_delivery_owed_limit || 1000}
                      onChange={(e) => setSettings({...settings, max_food_delivery_owed_limit: parseFloat(e.target.value)})}
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Block food deliveries when COD collections owed exceeds this amount
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="block_food_delivery"
                      checked={settings?.block_food_delivery_on_limit !== false}
                      onChange={(e) => setSettings({...settings, block_food_delivery_on_limit: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <div>
                      <Label htmlFor="block_food_delivery" className="cursor-pointer">Enable Delivery Blocking</Label>
                      <p className="text-xs text-muted-foreground">
                        Block drivers from accepting food delivery orders when limit exceeded
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Driver Commission Options - Which options are available to drivers */}
          <div className="bg-card rounded-xl border border-border p-4 md:p-6 md:col-span-2 mt-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Settings2 className="w-5 h-5" /> Driver Commission Options
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Control which commission plans drivers can select from when requesting a change.
              Fixed plans (Daily/Weekly/Monthly) are <strong>prepaid</strong> - driver must pay before switching.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">Percentage per Ride</p>
                  <p className="text-xs text-muted-foreground">% of each ride fare</p>
                </div>
                <Switch
                  checked={commissionOptions.percentage_enabled}
                  onCheckedChange={(v) => setCommissionOptions({...commissionOptions, percentage_enabled: v})}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">Flat per Ride</p>
                  <p className="text-xs text-muted-foreground">Fixed ₹ per ride</p>
                </div>
                <Switch
                  checked={commissionOptions.flat_per_ride_enabled}
                  onCheckedChange={(v) => setCommissionOptions({...commissionOptions, flat_per_ride_enabled: v})}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div>
                  <p className="font-medium text-blue-700">Fixed Daily</p>
                  <p className="text-xs text-blue-600">Prepaid per day</p>
                </div>
                <Switch
                  checked={commissionOptions.fixed_daily_enabled}
                  onCheckedChange={(v) => setCommissionOptions({...commissionOptions, fixed_daily_enabled: v})}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div>
                  <p className="font-medium text-blue-700">Fixed Weekly</p>
                  <p className="text-xs text-blue-600">Prepaid per week</p>
                </div>
                <Switch
                  checked={commissionOptions.fixed_weekly_enabled}
                  onCheckedChange={(v) => setCommissionOptions({...commissionOptions, fixed_weekly_enabled: v})}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div>
                  <p className="font-medium text-green-700">Fixed Monthly</p>
                  <p className="text-xs text-green-600">Prepaid per month</p>
                </div>
                <Switch
                  checked={commissionOptions.fixed_monthly_enabled}
                  onCheckedChange={(v) => setCommissionOptions({...commissionOptions, fixed_monthly_enabled: v})}
                />
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              <Button 
                onClick={handleSaveCommissionOptions} 
                variant="default"
                disabled={savingCommissionOptions}
              >
                {savingCommissionOptions ? "Saving..." : "Save Commission Plan Options"}
              </Button>
            </div>
          </div>

          {/* Payment Gateway Settings for Commission */}
          <div className="bg-card rounded-xl border border-border p-4 md:p-6 md:col-span-2 mt-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> Subscription Payment Settings
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Configure how drivers pay when switching from per-ride to subscription (Daily/Weekly/Monthly) plans.
            </p>
            
            {/* Payment Mode Selection */}
            <div className="mb-6">
              <Label className="text-base font-medium">Commission Plan Switch Mode</Label>
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <div 
                  onClick={() => setPaymentSettings({...paymentSettings, commission_payment_mode: "prepaid"})}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    paymentSettings.commission_payment_mode === "prepaid" 
                      ? "border-primary bg-primary/5" 
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      paymentSettings.commission_payment_mode === "prepaid" 
                        ? "border-primary bg-primary" 
                        : "border-gray-300"
                    }`}>
                      {paymentSettings.commission_payment_mode === "prepaid" && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="font-semibold">Pre-Paid (Instant)</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Drivers must pay via Razorpay before switching. <strong>Auto-approved</strong> on payment success.
                  </p>
                </div>
                
                <div 
                  onClick={() => setPaymentSettings({...paymentSettings, commission_payment_mode: "postpaid"})}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    paymentSettings.commission_payment_mode === "postpaid" 
                      ? "border-primary bg-primary/5" 
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      paymentSettings.commission_payment_mode === "postpaid" 
                        ? "border-primary bg-primary" 
                        : "border-gray-300"
                    }`}>
                      {paymentSettings.commission_payment_mode === "postpaid" && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="font-semibold">Post-Paid (Approval)</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Subscription amount added to driver's dues. <strong>Admin approval required</strong> to switch.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Razorpay Keys */}
            {paymentSettings.commission_payment_mode === "prepaid" && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  Razorpay API Keys
                  {paymentSettings.has_razorpay_configured ? (
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" /> Configured
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Not Configured
                    </span>
                  )}
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Razorpay Key ID</Label>
                    <Input
                      type="text"
                      value={paymentSettings.razorpay_key_id}
                      onChange={(e) => setPaymentSettings({...paymentSettings, razorpay_key_id: e.target.value})}
                      placeholder="rzp_live_xxxxxxxxxx"
                      className="mt-1"
                      data-testid="razorpay-key-id-input"
                    />
                  </div>
                  <div>
                    <Label>Razorpay Key Secret</Label>
                    <Input
                      type="password"
                      value={paymentSettings.razorpay_key_secret}
                      onChange={(e) => setPaymentSettings({...paymentSettings, razorpay_key_secret: e.target.value})}
                      placeholder={paymentSettings.has_razorpay_configured ? "••••••••••" : "Enter secret key"}
                      className="mt-1"
                      data-testid="razorpay-key-secret-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Get your Razorpay keys from <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener" className="text-primary underline">dashboard.razorpay.com</a>. 
                  For testing, use Test mode keys.
                </p>
              </div>
            )}
            
            <div className="flex justify-end mt-4">
              <Button onClick={handleSavePaymentSettings} disabled={savingPaymentSettings}>
                {savingPaymentSettings ? "Saving..." : "Save Payment Settings"}
              </Button>
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button onClick={handleSaveSettings}>Save Settings</Button>
          </div>
        </TabsContent>

        {/* Commission Change Requests Tab */}
        <TabsContent value="change-requests">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <History className="w-5 h-5 text-blue-500" />
                Driver Commission Change Requests
              </h3>
              <span className="text-sm text-muted-foreground">
                {changeRequests.filter(r => r.status === "pending").length} pending request(s)
              </span>
            </div>
            
            {changeRequests.length === 0 ? (
              <div className="bg-muted/50 rounded-lg p-8 text-center">
                <History className="w-12 h-12 mx-auto mb-3 text-gray-400 opacity-50" />
                <p className="text-muted-foreground">No commission change requests</p>
              </div>
            ) : (
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Driver</TableHead>
                      <TableHead>Current</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {changeRequests.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{req.driver_name}</p>
                            <p className="text-xs text-muted-foreground">{req.driver_phone}</p>
                          </div>
                        </TableCell>
                        <TableCell className="capitalize">
                          {req.current_type?.replace(/_/g, ' ')}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            req.requested_type?.includes('fixed') 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {req.requested_type?.replace(/_/g, ' ')}
                          </span>
                        </TableCell>
                        <TableCell>
                          {req.requires_payment ? (
                            <div className="text-sm">
                              <p className={req.payment_verified ? 'text-green-600' : 'text-amber-600'}>
                                ₹{req.required_amount}
                              </p>
                              {req.payment_reference && (
                                <p className="text-xs text-muted-foreground">Ref: {req.payment_reference}</p>
                              )}
                              {req.payment_verified ? (
                                <span className="text-xs text-green-600 flex items-center gap-1">
                                  <Check className="w-3 h-3" /> Verified
                                </span>
                              ) : (
                                <span className="text-xs text-amber-600 flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" /> Pending
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">Not required</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            req.status === 'approved' ? 'bg-green-100 text-green-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {req.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(req.created_at).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right">
                          {req.status === 'pending' && (
                            <div className="flex items-center justify-end gap-2">
                              {req.requires_payment && !req.payment_verified && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-blue-600"
                                  onClick={() => {
                                    setSelectedRequest(req);
                                    setShowVerifyPaymentDialog(true);
                                  }}
                                >
                                  <CreditCard className="w-4 h-4 mr-1" />
                                  Verify
                                </Button>
                              )}
                              {(!req.requires_payment || req.payment_verified) && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-green-600"
                                  onClick={() => handleApproveChangeRequest(req.id)}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600"
                                onClick={() => handleRejectChangeRequest(req.id)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Pending Approvals Tab */}
        <TabsContent value="pending">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Pending Driver Payments
              </h3>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchData}
                  className="text-xs"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </Button>
                <span className="text-sm text-muted-foreground">
                  {pendingPayments.length} payment(s) awaiting approval
                </span>
              </div>
            </div>
            
            {pendingPayments.length === 0 ? (
              <div className="bg-muted/50 rounded-lg p-8 text-center">
                <Check className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                <p className="text-muted-foreground">No pending payments to approve</p>
              </div>
            ) : (
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Driver</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Proof</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingPayments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="font-medium">{payment.recipient_name || payment.payer_name}</TableCell>
                        <TableCell className="font-semibold text-green-600">₹{payment.amount}</TableCell>
                        <TableCell className="capitalize">{payment.payment_method}</TableCell>
                        <TableCell>{payment.reference_number || "-"}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(payment.recorded_at).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewPaymentProof(payment.id)}
                          >
                            <Image className="w-4 h-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleApprovePayment(payment.id)}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleRejectPayment(payment.id)}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Drivers Tab */}
        <TabsContent value="drivers">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or phone number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="driver-search-input"
              />
            </div>
            <div className="text-sm text-muted-foreground flex items-center">
              {driversLoading ? (
                <span>Loading...</span>
              ) : (
                <span>Showing {drivers.length} of {driversTotalCount} drivers</span>
              )}
            </div>
          </div>
          
          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {driversLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading drivers...</div>
            ) : drivers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No drivers found</div>
            ) : drivers.map(driver => {
                // Format commission type and amount
                const vehicleType = driver.vehicle_type || 'bike';
                const vehicleRates = settings?.vehicle_rates?.[vehicleType] || {};
                const defaultFlatRate = vehicleType === 'auto' ? 15 : vehicleType === 'car' ? 20 : vehicleType === 'goods_carrier' ? 25 : 10;
                
                const commType = driver.commission_override?.commission_type || settings?.commission_type || 'flat_per_ride';
                const commValue = commType === 'percentage' 
                  ? (driver.commission_override?.percentage_rate ?? vehicleRates.percentage_rate ?? 10)
                  : commType === 'flat_per_ride'
                  ? (driver.commission_override?.flat_per_ride ?? vehicleRates.flat_per_ride ?? defaultFlatRate)
                  : commType === 'fixed_daily'
                  ? (driver.commission_override?.fixed_daily ?? vehicleRates.fixed_daily ?? 50)
                  : commType === 'fixed_weekly'
                  ? (driver.commission_override?.fixed_weekly ?? vehicleRates.fixed_weekly ?? 200)
                  : commType === 'fixed_monthly'
                  ? (driver.commission_override?.fixed_monthly ?? vehicleRates.fixed_monthly ?? 500)
                  : 10;
                
                const commDisplay = commType === 'percentage' 
                  ? `${commValue}%` 
                  : commType === 'flat_per_ride'
                  ? `₹${commValue}/ride`
                  : commType === 'fixed_daily'
                  ? `₹${commValue}/day`
                  : commType === 'fixed_weekly'
                  ? `₹${commValue}/week`
                  : commType === 'fixed_monthly'
                  ? `₹${commValue}/month`
                  : `${commValue}`;
                
                // Format joined date
                const joinedDate = driver.created_at 
                  ? new Date(driver.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                  : '-';
                
                return (
                  <div key={driver.id} className="bg-card rounded-xl border border-border p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium">{driver.name}</p>
                        <p className="text-xs text-muted-foreground">{driver.phone}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          driver.commission_override ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {driver.commission_override ? 'Custom' : 'Default'}
                        </span>
                        <span className="text-xs text-muted-foreground font-medium">{commDisplay}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground mb-3">
                      <div>
                        <span className="block text-[10px] text-muted-foreground/60">Vehicle</span>
                        <span className="capitalize">{driver.vehicle_type || '-'}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] text-muted-foreground/60">City</span>
                        <span>{driver.city_name || '-'}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] text-muted-foreground/60">Joined</span>
                        <span>{joinedDate}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => openOverrideDialog(driver, "driver")}
                      >
                        Manage Commission
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-500 border-red-200 hover:bg-red-50"
                        onClick={() => handleRemoveDriver(driver, false)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
          </div>
          
          {/* Desktop Table */}
          <div className="hidden md:block bg-card rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setDriversSortConfig(prev => ({
                      key: 'name',
                      direction: prev.key === 'name' && prev.direction === 'asc' ? 'desc' : 'asc'
                    }))}
                  >
                    <div className="flex items-center gap-1">
                      Driver
                      {driversSortConfig.key === 'name' ? (
                        driversSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      ) : <ArrowUpDown className="w-4 h-4 opacity-30" />}
                    </div>
                  </TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setDriversSortConfig(prev => ({
                      key: 'vehicle_type',
                      direction: prev.key === 'vehicle_type' && prev.direction === 'asc' ? 'desc' : 'asc'
                    }))}
                  >
                    <div className="flex items-center gap-1">
                      Vehicle
                      {driversSortConfig.key === 'vehicle_type' ? (
                        driversSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      ) : <ArrowUpDown className="w-4 h-4 opacity-30" />}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setDriversSortConfig(prev => ({
                      key: 'city_name',
                      direction: prev.key === 'city_name' && prev.direction === 'asc' ? 'desc' : 'asc'
                    }))}
                  >
                    <div className="flex items-center gap-1">
                      City
                      {driversSortConfig.key === 'city_name' ? (
                        driversSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      ) : <ArrowUpDown className="w-4 h-4 opacity-30" />}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setDriversSortConfig(prev => ({
                      key: 'created_at',
                      direction: prev.key === 'created_at' && prev.direction === 'asc' ? 'desc' : 'asc'
                    }))}
                  >
                    <div className="flex items-center gap-1">
                      Joined
                      {driversSortConfig.key === 'created_at' ? (
                        driversSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      ) : <ArrowUpDown className="w-4 h-4 opacity-30" />}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setDriversSortConfig(prev => ({
                      key: 'commission_type',
                      direction: prev.key === 'commission_type' && prev.direction === 'asc' ? 'desc' : 'asc'
                    }))}
                  >
                    <div className="flex items-center gap-1">
                      Commission
                      {driversSortConfig.key === 'commission_type' ? (
                        driversSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      ) : <ArrowUpDown className="w-4 h-4 opacity-30" />}
                    </div>
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {driversLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Loading drivers...
                    </TableCell>
                  </TableRow>
                ) : drivers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No drivers found
                    </TableCell>
                  </TableRow>
                ) : drivers
                  .sort((a, b) => {
                    const { key, direction } = driversSortConfig;
                    let aVal, bVal;
                    
                    if (key === 'commission_type') {
                      aVal = a.commission_override ? 'Custom' : 'Default';
                      bVal = b.commission_override ? 'Custom' : 'Default';
                    } else if (key === 'created_at') {
                      aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
                      bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
                    } else {
                      aVal = (a[key] || '').toString().toLowerCase();
                      bVal = (b[key] || '').toString().toLowerCase();
                    }
                    
                    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
                    return 0;
                  })
                  .map(driver => {
                    // Format commission type and amount
                    const vehicleType = driver.vehicle_type || 'bike';
                    const vehicleRates = settings?.vehicle_rates?.[vehicleType] || {};
                    const defaultFlatRate = vehicleType === 'auto' ? 15 : vehicleType === 'car' ? 20 : vehicleType === 'goods_carrier' ? 25 : 10;
                    
                    const commType = driver.commission_override?.commission_type || settings?.commission_type || 'flat_per_ride';
                    const commValue = commType === 'percentage' 
                      ? (driver.commission_override?.percentage_rate ?? vehicleRates.percentage_rate ?? 10)
                      : commType === 'flat_per_ride'
                      ? (driver.commission_override?.flat_per_ride ?? vehicleRates.flat_per_ride ?? defaultFlatRate)
                      : commType === 'fixed_daily'
                      ? (driver.commission_override?.fixed_daily ?? vehicleRates.fixed_daily ?? 50)
                      : commType === 'fixed_weekly'
                      ? (driver.commission_override?.fixed_weekly ?? vehicleRates.fixed_weekly ?? 200)
                      : commType === 'fixed_monthly'
                      ? (driver.commission_override?.fixed_monthly ?? vehicleRates.fixed_monthly ?? 500)
                      : 10;
                    
                    const commDisplay = commType === 'percentage' 
                      ? `${commValue}%` 
                      : commType === 'flat_per_ride'
                      ? `₹${commValue}/ride`
                      : commType === 'fixed_daily'
                      ? `₹${commValue}/day`
                      : commType === 'fixed_weekly'
                      ? `₹${commValue}/week`
                      : commType === 'fixed_monthly'
                      ? `₹${commValue}/month`
                      : `${commValue}`;
                    
                    // Format joined date
                    const joinedDate = driver.created_at 
                      ? new Date(driver.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '-';
                    
                    return (
                      <TableRow key={driver.id}>
                        <TableCell className="font-medium">{driver.name}</TableCell>
                        <TableCell>{driver.phone}</TableCell>
                        <TableCell>
                          <span className="capitalize text-sm">{driver.vehicle_type || '-'}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">{driver.city_name || '-'}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">{joinedDate}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className={`px-2 py-1 rounded-full text-xs w-fit ${
                              driver.commission_override ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {driver.commission_override ? 'Custom' : 'Default'}
                            </span>
                            <span className="text-xs text-muted-foreground font-medium">{commDisplay}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleLoginAsDriver(driver)}
                              disabled={impersonating}
                              title="Login as this Driver"
                              data-testid={`login-as-driver-${driver.id}`}
                            >
                              <LogIn className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openOverrideDialog(driver, "driver")}>
                              Manage
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoveDriver(driver, false)}
                              title="Remove driver from your list"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination Controls */}
          {driversTotalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-2">
              <p className="text-sm text-muted-foreground">
                Page {driversPage} of {driversTotalPages} ({driversTotalCount} total)
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newPage = driversPage - 1;
                    setDriversPage(newPage);
                    fetchPaginatedDrivers(newPage, searchTerm);
                  }}
                  disabled={driversPage <= 1 || driversLoading}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newPage = driversPage + 1;
                    setDriversPage(newPage);
                    fetchPaginatedDrivers(newPage, searchTerm);
                  }}
                  disabled={driversPage >= driversTotalPages || driversLoading}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* City Admins Tab */}
        <TabsContent value="city-admins">
          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {cityAdmins.map(ca => (
              <div key={ca.id} className="bg-card rounded-xl border border-border p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium">{ca.name}</p>
                    <p className="text-xs text-muted-foreground">{ca.city_name}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    ca.commission_override ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {ca.commission_override ? 'Custom' : 'Default'}
                  </span>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="w-full"
                  onClick={() => openOverrideDialog(ca, "city_admin")}
                >
                  Manage Commission
                </Button>
              </div>
            ))}
            {cityAdmins.length === 0 && (
              <div className="bg-card rounded-xl border border-border p-6 text-center text-muted-foreground">
                No city admins found
              </div>
            )}
          </div>
          
          {/* Desktop Table */}
          <div className="hidden md:block bg-card rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>City Admin</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cityAdmins.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No city admins found
                    </TableCell>
                  </TableRow>
                ) : (
                  cityAdmins.map(ca => (
                    <TableRow key={ca.id}>
                      <TableCell className="font-medium">{ca.name}</TableCell>
                      <TableCell>{ca.city_name}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          ca.commission_override ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {ca.commission_override ? 'Custom' : 'Default'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" onClick={() => openOverrideDialog(ca, "city_admin")}>
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {payments.map(payment => {
              const typeInfo = PAYMENT_TYPES.find(t => t.id === payment.payment_type);
              return (
                <div key={payment.id} className="bg-card rounded-xl border border-border p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      {typeInfo && <typeInfo.icon className={`w-4 h-4 ${typeInfo.color}`} />}
                      <span className="font-medium">{typeInfo?.name}</span>
                    </div>
                    <span className={`font-bold ${typeInfo?.color}`}>
                      ₹{payment.amount}
                    </span>
                  </div>
                  <p className="text-sm">{payment.recipient_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(payment.recorded_at).toLocaleDateString()} • {payment.payment_method}
                  </p>
                  {payment.reference_number && (
                    <p className="text-xs text-muted-foreground">Ref: {payment.reference_number}</p>
                  )}
                </div>
              );
            })}
            {payments.length === 0 && (
              <div className="bg-card rounded-xl border border-border p-6 text-center text-muted-foreground">
                No payments recorded yet
              </div>
            )}
          </div>
          
          {/* Desktop Table */}
          <div className="hidden md:block bg-card rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Reference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No payments recorded yet
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map(payment => {
                    const typeInfo = PAYMENT_TYPES.find(t => t.id === payment.payment_type);
                    return (
                      <TableRow key={payment.id}>
                        <TableCell>{new Date(payment.recorded_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {typeInfo && <typeInfo.icon className={`w-4 h-4 ${typeInfo.color}`} />}
                            <span>{typeInfo?.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>{payment.recipient_name}</TableCell>
                        <TableCell className={`font-medium ${typeInfo?.color}`}>₹{payment.amount}</TableCell>
                        <TableCell className="capitalize">{payment.payment_method}</TableCell>
                        <TableCell>{payment.reference_number || "-"}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={(open) => {
        setShowPaymentDialog(open);
        if (!open) {
          setDriverSearch("");
          setCityFilter("");
          setSelectedDriverDue(null);
        }
      }}>
        <DialogContent className="max-w-md w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Payment Type</Label>
              <select
                value={paymentForm.payment_type}
                onChange={(e) => {
                  setPaymentForm({...paymentForm, payment_type: e.target.value, recipient_id: "", amount: ""});
                  setSelectedDriverDue(null);
                  setDriverSearch("");
                }}
                className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
              >
                {PAYMENT_TYPES.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            
            {paymentForm.payment_type !== "admin_to_city_admin" && (
              <>
                {/* Search and Filter */}
                <div className="space-y-2">
                  <Label>Search Driver</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search by name or phone..."
                        value={driverSearch}
                        onChange={(e) => setDriverSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  {cities.length > 0 && (
                    <select
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                    >
                      <option value="">All Cities</option>
                      {cities.map(city => (
                        <option key={city.id} value={city.id}>{city.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                
                {/* Driver Selection */}
                <div>
                  <Label>Select Driver ({filteredPaymentDrivers.length} found)</Label>
                  <select
                    value={paymentForm.recipient_id}
                    onChange={(e) => handleDriverSelect(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                    size={Math.min(5, filteredPaymentDrivers.length + 1)}
                  >
                    <option value="">-- Select Driver --</option>
                    {filteredPaymentDrivers.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.name} • {d.phone} {d.assigned_city_name ? `• ${d.assigned_city_name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Show driver's due amount */}
                {loadingDriverDue && (
                  <div className="text-sm text-muted-foreground">Loading due amount...</div>
                )}
                {selectedDriverDue && !loadingDriverDue && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-medium text-amber-800">
                      {selectedDriverDue.driver_name}'s Outstanding Due:
                    </p>
                    <p className="text-2xl font-bold text-amber-900">
                      ₹{selectedDriverDue.amount.toFixed(2)}
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      Amount auto-filled. You can edit if driver paid a different amount.
                    </p>
                  </div>
                )}
              </>
            )}
            
            {paymentForm.payment_type === "admin_to_city_admin" && (
              <div>
                <Label>City Admin</Label>
                <select
                  value={paymentForm.recipient_id}
                  onChange={(e) => setPaymentForm({...paymentForm, recipient_id: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                >
                  <option value="">Select...</option>
                  {cityAdmins.map(ca => (
                    <option key={ca.id} value={ca.id}>{ca.name}</option>
                  ))}
                </select>
              </div>
            )}
            
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                className="mt-1"
                placeholder="Enter amount"
              />
            </div>
            
            <div>
              <Label>Payment Method</Label>
              <select
                value={paymentForm.payment_method}
                onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
              >
                {PAYMENT_METHODS.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Reference Number (Optional)</Label>
              <Input
                value={paymentForm.reference_number}
                onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})}
                placeholder="Transaction ID, UPI Ref, etc."
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                placeholder="Additional notes..."
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment}>Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent className="max-w-md w-[95vw]">
          <DialogHeader>
            <DialogTitle>
              Commission - {selectedEntity?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEntity && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">Current Effective Rate</p>
                <p className="text-xs text-muted-foreground">
                  {selectedEntity.effective?.commission_type === "percentage" 
                    ? `${selectedEntity.effective?.percentage_rate || 10}% per ride`
                    : selectedEntity.effective?.commission_type === "flat_per_ride"
                    ? `₹${selectedEntity.effective?.flat_per_ride || 10} per ride`
                    : selectedEntity.effective?.commission_type === "fixed_daily"
                    ? `₹${selectedEntity.effective?.fixed_daily || 50} per day`
                    : selectedEntity.effective?.commission_type === "fixed_weekly"
                    ? `₹${selectedEntity.effective?.fixed_weekly || 200} per week`
                    : selectedEntity.effective?.commission_type === "fixed_monthly"
                    ? `₹${selectedEntity.effective?.fixed_monthly || 500} per month`
                    : `₹${selectedEntity.effective?.flat_per_ride || 10} per ride (default)`
                  }
                </p>
                {selectedEntity.has_override && (
                  <p className="text-xs text-blue-600 mt-1">✓ Custom override applied</p>
                )}
              </div>
              
              <div>
                <Label>Override Commission Type</Label>
                <select
                  value={selectedEntity.override?.commission_type || ""}
                  onChange={(e) => setSelectedEntity({
                    ...selectedEntity,
                    override: {...selectedEntity.override, commission_type: e.target.value || null}
                  })}
                  className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                >
                  <option value="">Use Default</option>
                  {COMMISSION_TYPES.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              
              {selectedEntity.override?.commission_type === "percentage" && (
                <div>
                  <Label>Percentage Rate (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={selectedEntity.override?.percentage_rate || ""}
                    onChange={(e) => setSelectedEntity({
                      ...selectedEntity,
                      override: {...selectedEntity.override, percentage_rate: parseFloat(e.target.value) || null}
                    })}
                    className="mt-1"
                  />
                </div>
              )}
              
              {selectedEntity.override?.commission_type === "flat_per_ride" && (
                <div>
                  <Label>Amount per Ride (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={selectedEntity.override?.flat_per_ride || ""}
                    onChange={(e) => setSelectedEntity({
                      ...selectedEntity,
                      override: {...selectedEntity.override, flat_per_ride: parseFloat(e.target.value) || null}
                    })}
                    className="mt-1"
                  />
                </div>
              )}
              
              {selectedEntity.override?.commission_type === "fixed_daily" && (
                <div>
                  <Label>Daily Amount (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={selectedEntity.override?.fixed_daily || ""}
                    onChange={(e) => setSelectedEntity({
                      ...selectedEntity,
                      override: {...selectedEntity.override, fixed_daily: parseFloat(e.target.value) || null}
                    })}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Applied only if driver has at least 1 ride that day</p>
                </div>
              )}
              
              {selectedEntity.override?.commission_type === "fixed_weekly" && (
                <div>
                  <Label>Weekly Amount (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={selectedEntity.override?.fixed_weekly || ""}
                    onChange={(e) => setSelectedEntity({
                      ...selectedEntity,
                      override: {...selectedEntity.override, fixed_weekly: parseFloat(e.target.value) || null}
                    })}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Applied only if driver has at least 1 ride that week</p>
                </div>
              )}
              
              {selectedEntity.override?.commission_type === "fixed_monthly" && (
                <div>
                  <Label>Monthly Amount (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={selectedEntity.override?.fixed_monthly || ""}
                    onChange={(e) => setSelectedEntity({
                      ...selectedEntity,
                      override: {...selectedEntity.override, fixed_monthly: parseFloat(e.target.value) || null}
                    })}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Applied only if driver has at least 1 ride that month</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverrideDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveOverride}>Save Override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify Payment Dialog */}
      <Dialog open={showVerifyPaymentDialog} onOpenChange={setShowVerifyPaymentDialog}>
        <DialogContent className="max-w-md w-[95vw]">
          <DialogHeader>
            <DialogTitle>Verify Payment</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800">Commission Change Request</h4>
                <p className="text-sm text-blue-700 mt-1">
                  <strong>{selectedRequest.driver_name}</strong> wants to switch from{" "}
                  <span className="capitalize">{selectedRequest.current_type?.replace(/_/g, " ")}</span> to{" "}
                  <span className="capitalize font-semibold">{selectedRequest.requested_type?.replace(/_/g, " ")}</span>
                </p>
              </div>
              
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-amber-800 font-medium">Required Amount</span>
                  <span className="text-2xl font-bold text-amber-700">₹{selectedRequest.required_amount}</span>
                </div>
                {selectedRequest.payment_reference && (
                  <div className="text-sm text-amber-700 mt-2">
                    <strong>Reference:</strong> {selectedRequest.payment_reference}
                  </div>
                )}
                {selectedRequest.payment_amount && (
                  <div className="text-sm text-amber-700">
                    <strong>Driver Reported:</strong> ₹{selectedRequest.payment_amount}
                  </div>
                )}
              </div>
              
              <p className="text-sm text-muted-foreground">
                Verify that you have received the payment from the driver. Once verified, you can approve
                their commission change request.
              </p>
            </div>
          )}
          
          <DialogFooter className="flex flex-col sm:flex-row gap-2 w-full">
            <Button variant="outline" onClick={() => setShowVerifyPaymentDialog(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleVerifyPayment(selectedRequest?.id, false)}
              className="w-full sm:w-auto"
            >
              <X className="w-4 h-4 mr-1" />
              Reject
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
              onClick={() => handleVerifyPayment(selectedRequest?.id, true)}
            >
              <Check className="w-4 h-4 mr-1" />
              Verify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommissionManagement;
